import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as dmsService from "../../../services/dmsService";
import useModuleAccess from "../../../hooks/useModuleAccess";
import { hasModulePermission, isModuleEnabled } from "../../../constants/platformModules";
import useDmsOptions from "../useDmsOptions";
import { fmtMoney } from "../../Fin/gl/glContext";
import { act } from "../../Fin/gl/glUi";
import { FormDrawer, useLoad } from "../../../components/ui";

// On a lead (enquiry), for dealers: the vehicles the customer is interested in, and the next
// steps - a quotation or a test drive. The lead becomes a Dealership prospect the first time.
const heading = { fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#C9194A", margin: "4px 0 12px" };

export default function LeadVehiclePanel({ lead }) {
  const { access } = useModuleAccess();
  const navigate = useNavigate();
  const enabled = isModuleEnabled(access, "DMS") && hasModulePermission(access, "DMS", "DMS_SALES", "DMS_SALES_MANAGER", "DMS_ADMIN");
  const interest = useLoad(() => (enabled ? dmsService.listInterest({ lead_id: lead.id }) : Promise.resolve({ success: true, data: [] })), [lead.id, enabled]);
  const cat = useDmsOptions(enabled ? ["variants", "colours:ext"] : [], 0, { ext: { colour_type: "EXTERIOR" } });
  const [adding, setAdding] = useState(false);
  if (!enabled) return null;

  const go = async (to) => {
    const res = await act(() => dmsService.prospectFromLead(lead.id), "Prospect ready");
    if (res && res.data) navigate(`${to}?prospect=${encodeURIComponent(res.data.id)}&lead=${encodeURIComponent(lead.id)}`);
  };
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={heading}>Vehicle interest</div>
      {(interest.data || []).map((i) => (
        <div key={i.id} style={{ fontSize: 13, marginBottom: 6 }}>
          <b>{[i.make_name, i.model_name, i.variant_name].filter(Boolean).join(" ") || "Any model"}</b>
          {i.ext_colour_name ? ` · ${i.ext_colour_name}` : ""}
          {i.budget ? ` · budget ${fmtMoney(i.budget, 0)}` : ""}
          {i.payment_type ? ` · ${i.payment_type.toLowerCase().replace(/_/g, " ")}` : ""}
          {i.trade_in_offered ? " · has a trade-in" : ""}
          {i.notes ? <div style={{ color: "#6B7280" }}>{i.notes}</div> : null}
        </div>
      ))}
      <div className="d-flex flex-wrap gap-2 mt-2">
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setAdding(true)}>
          Add vehicle interest
        </button>
        <button type="button" className="btn Button primaryBackground text-white btn-sm" onClick={() => go("/dms/quotes")}>
          Create quotation
        </button>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => go("/dms/test-drives")}>
          Book test drive
        </button>
      </div>
      <FormDrawer
        show={adding}
        title="Vehicle interest"
        initial={{ payment_type: "CASH" }}
        onHide={() => setAdding(false)}
        onDone={interest.reload}
        onSave={(clean, raw) => dmsService.addInterest({ ...clean, lead_id: lead.id, budget: clean.budget ? Number(clean.budget) : undefined, trade_in_offered: !!raw.trade_in_offered })}
        fields={[
          { key: "variant_id", label: "Variant", type: "select", options: cat.variants || [] },
          { key: "ext_colour_id", label: "Colour", type: "select", options: cat.ext || [] },
          { key: "budget", label: "Budget", type: "number", min: 0 },
          {
            key: "payment_type",
            label: "Paying by",
            type: "select",
            options: [
              { label: "Cash", value: "CASH" },
              { label: "Bank finance", value: "BANK_FINANCE" },
              { label: "Card", value: "CARD" },
              { label: "Bank transfer", value: "BANK_TRANSFER" },
            ],
          },
          { key: "trade_in_offered", label: "Has a vehicle to trade in", type: "checkbox" },
          { key: "notes", label: "Notes", type: "textarea", max: 500 },
        ]}
      />
    </div>
  );
}
